/**
 * UTM tagging helper.
 *
 * Appends `utm_*` params to a URL at click/copy time so the same stored link can
 * be attributed differently per context (which domain shared it, which post
 * style, which CTA). CapyStats then surfaces these under
 * `/stats/breakdown?property=utm_source|utm_medium|utm_campaign`.
 *
 * NO-PII: only pass non-personal labels (domain slug, property slug, cta type) —
 * never an email, name, phone or any visitor identifier.
 */
export interface UtmParams {
  /** WHERE the click originates: owner domain slug, 'zaklepuj', or 'panel'. */
  source: string;
  /** The link/channel type: social | share | cta | footer | email. */
  medium: string;
  /** The business intent: e.g. content_generator, property_share, primary_cta. */
  campaign: string;
  /** The specific target: property slug, cta action, page name. */
  content?: string;
  /** Optional keyword/term. */
  term?: string;
}

/** Base used to resolve relative URLs into absolute ones before tagging. */
function fallbackOrigin(): string {
  return typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://zaklepuj.pl';
}

/**
 * Returns `url` with `utm_*` params appended (existing `utm_*` keys are
 * overwritten). Non-web schemes (`tel:`, `mailto:`, `sms:`…) and malformed URLs
 * are returned unchanged, so it is always safe to call.
 */
export function appendUtm(url: string, params: UtmParams): string {
  if (!url) {
    return url;
  }
  try {
    const u = new URL(url, fallbackOrigin());
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return url;
    }
    u.searchParams.set('utm_source', params.source);
    u.searchParams.set('utm_medium', params.medium);
    u.searchParams.set('utm_campaign', params.campaign);
    if (params.content) {
      u.searchParams.set('utm_content', params.content);
    }
    if (params.term) {
      u.searchParams.set('utm_term', params.term);
    }
    return u.toString();
  } catch {
    return url;
  }
}
